import React, { forwardRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

const cleanThoughtLine = (value) => String(value || '').trim().replace(/\s+/g, ' ');

const isCompactRuntimeSystemEvent = (msg) => {
  if (!msg || msg.role !== 'system') return false;
  const origin = String(msg.origin || '').trim().toLowerCase();
  const source = String(msg.meta?.source || '').trim().toLowerCase();
  const text = String(msg.text || msg.message || '').trim();
  if (!text) return false;
  if (origin === 'runtime') return true;
  if (String(msg.thread_id || '').trim() === 'runtime-monitor') return true;
  if (source.startsWith('backend:') || source.startsWith('frontend:auto')) return true;
  return /^([📊🟡🟢🔴❓⚠✅▶⏳↪🧭])/.test(text);
};

const isLowSignalRuntimeEvent = (msg) => {
  if (!isCompactRuntimeSystemEvent(msg)) return false;
  const text = String(msg?.text || msg?.message || '').trim();
  if (!text) return false;
  if (/^🧭\s+(?:chat|runner|runtime|status)\//i.test(text)) return true;
  if (/^🟡 Backend tick started\b/.test(text)) return true;
  if (/^🟢 Backend tick finished\b/.test(text)) return true;
  if (/^📊 (Chat )?KPI\(w=\d+\):/.test(text)) return true;
  return false;
};

const RuntimeHud = ({ liveTraceStatus }) => {
  const phase = liveTraceStatus?.runtimePhase;
  if (!phase) return null;
  const actionProof = liveTraceStatus?.lastActionProof;
  return (
    <Box
      className={`cycle-phase-panel chat-runtime-hud is-${phase.status}`}
      data-testid="chat-runtime-hud"
    >
      <Box className="cycle-phase-panel__head">
        <Box>
          <Typography variant="caption" className="section-kicker">
            Runtime now
          </Typography>
          <Typography variant="body2" className="cycle-phase-panel__headline">
            {phase.status === 'running' ? 'Сейчас' : 'Состояние'}: {phase.phaseLabel}
          </Typography>
        </Box>
        <Box className="cycle-phase-panel__meta">
          {liveTraceStatus.participantName ? <span>участник {liveTraceStatus.participantName}</span> : null}
          {liveTraceStatus.runtimeStageRole ? <span>role {liveTraceStatus.runtimeStageRole}</span> : null}
          {phase.elapsedS > 0 ? <span>{Math.round(phase.elapsedS)}s</span> : null}
          {phase.timeoutS > 0 ? <span>limit {Math.round(phase.timeoutS)}s</span> : null}
          {liveTraceStatus.runtimeStageLastError ? <span>error</span> : null}
        </Box>
      </Box>
      <Box className="cycle-phase-rail" role="list" aria-label="OCM cycle phase in chat">
        {phase.steps.map((step) => (
          <Box
            key={`chat-ocm-phase-${step.key}`}
            className={`cycle-phase-step is-${step.state}`}
            role="listitem"
          >
            <span className="cycle-phase-step__dot" />
            <span className="cycle-phase-step__label">{step.label}</span>
          </Box>
        ))}
      </Box>
      {actionProof ? (
        <Box
          className={`action-proof-strip is-${actionProof.status}`}
          data-testid="action-proof-panel"
        >
          <Box className="action-proof-strip__head">
            <Box>
              <Typography variant="caption" className="section-kicker">
                Последнее действие
              </Typography>
              <Typography variant="body2" className="action-proof-strip__headline">
                L1-action: {actionProof.statusLabel}
              </Typography>
            </Box>
            <Box className="cycle-phase-panel__meta action-proof-strip__meta">
              <span>cmd {actionProof.commandN}</span>
              <span>work {actionProof.workOrderN}</span>
              <span>adapter {actionProof.adapterN}</span>
              <span>feedback {actionProof.completedN || actionProof.feedbackN}</span>
              <span>reality {actionProof.realityN}</span>
              <span>gaps {actionProof.gapN}</span>
            </Box>
          </Box>
          <Box className="action-proof-rail" role="list" aria-label="Last L1 action proof">
            {actionProof.steps.map((step) => (
              <Box
                key={`chat-action-proof-${step.key}`}
                className={`cycle-phase-step action-proof-step is-${step.state}`}
                role="listitem"
              >
                <span className="cycle-phase-step__dot" />
                <span className="cycle-phase-step__label">{step.label}</span>
              </Box>
            ))}
          </Box>
          {(actionProof.detail || actionProof.surface) ? (
            <Typography variant="body2" className="action-proof-strip__detail">
              {actionProof.detail || 'result surface'}
              {actionProof.surface ? `, surface ${actionProof.surface}` : ''}
            </Typography>
          ) : null}
        </Box>
      ) : null}
      {(liveTraceStatus.runtimeStageHeadline || phase.detail || liveTraceStatus.runtimeStageLastError) ? (
        <Typography variant="body2" className="cycle-phase-panel__detail">
          {liveTraceStatus.runtimeStageLastError || liveTraceStatus.runtimeStageHeadline || phase.detail}
          {liveTraceStatus.runtimeStageDetail ? `, ${liveTraceStatus.runtimeStageDetail}` : ''}
        </Typography>
      ) : null}
    </Box>
  );
};

export const buildVisibleThoughtLines = (trace) => {
  const parseSummaryObject = (value) => {
    if (!value || typeof value !== 'string') return null;
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  };
  const explicit = Array.isArray(trace?.internal_tick_delta?.thoughts)
    ? trace.internal_tick_delta.thoughts.map(cleanThoughtLine).filter(Boolean)
    : [];
  if (explicit.length > 0) return explicit.slice(0, 3);

  const out = [];
  const seen = new Set();
  const roles = Array.isArray(trace?.roles) ? trace.roles : [];
  for (const roleRow of roles) {
    const role = String(roleRow?.role || '').trim().toLowerCase();
    const parsed = roleRow?.parsed && typeof roleRow.parsed === 'object'
      ? roleRow.parsed
      : parseSummaryObject(roleRow?.summary);
    if (!parsed || typeof parsed !== 'object') continue;
    const pushLine = (value, prefix = '') => {
      const text = cleanThoughtLine(value);
      if (!text) return;
      const line = prefix ? `${prefix}${text}` : text;
      const sig = line.toLowerCase();
      if (seen.has(sig)) return;
      seen.add(sig);
      out.push(line);
    };
    if (role === 'thinker') {
      for (const item of Array.isArray(parsed.insights) ? parsed.insights.slice(0, 2) : []) {
        pushLine(item);
      }
    } else if (role === 'interpret') {
      pushLine(parsed.summary, 'interpret: ');
    } else if (role === 'context_router') {
      const focus = Array.isArray(parsed.focus) ? parsed.focus[0] : '';
      pushLine(focus, 'continuation: ');
    }
    if (out.length >= 3) break;
  }
  return out.slice(0, 3);
};

const summarizeObjectFields = (row, fields, limit = 180) => {
  if (!row || typeof row !== 'object') return '';
  const parts = [];
  for (const field of fields) {
    const value = row[field];
    if (value === undefined || value === null || value === '') continue;
    const text = Array.isArray(value)
      ? value.map((item) => cleanThoughtLine(item)).filter(Boolean).join(',')
      : cleanThoughtLine(value);
    if (text) parts.push(`${field}=${text.slice(0, limit)}`);
  }
  return parts.join(', ');
};

const normalizeOutputControllerForKitchen = (packet) => {
  if (!packet || typeof packet !== 'object') return null;
  const distribution = packet.distribution && typeof packet.distribution === 'object'
    ? packet.distribution
    : {};
  const targets = Array.isArray(distribution.targets)
    ? distribution.targets
        .filter((row) => row && typeof row === 'object')
        .slice(0, 6)
        .map((row) => ({
          summary: summarizeObjectFields(
            row,
            ['kind', 'status', 'owner', 'participant_name', 'runtime_scope', 'command_kind', 'action_id', 'reason', 'output_source'],
            220
          )
        }))
    : [];
  return {
    packet_version: cleanThoughtLine(packet.packet_version),
    status: cleanThoughtLine(packet.status),
    source: cleanThoughtLine(packet.output_source),
    surface_kind: cleanThoughtLine(packet.surface_kind),
    recipient: summarizeObjectFields(
      packet.recipient,
      ['participant_name', 'participant_id', 'window_thread_id', 'runtime_scope'],
      180
    ),
    interlocutor: summarizeObjectFields(
      packet.interlocutor_signal,
      ['signal_id', 'speech_act', 'participant_name', 'window_thread_id'],
      180
    ),
    distribution: summarizeObjectFields(
      distribution,
      ['packet_version', 'ready_target_kinds', 'routed_target_kinds', 'deferred_target_kinds', 'blocked_target_kinds'],
      220
    ),
    targets,
    integrity: summarizeObjectFields(packet.integrity, ['valid', 'gap_n', 'scope'], 160),
    llm: summarizeObjectFields(packet.llm_wording, ['enabled', 'attempted', 'status', 'model'], 180),
  };
};

export const normalizeOperatorDigest = (trace) => {
  const digest = trace?.operator_digest && typeof trace.operator_digest === 'object'
    ? trace.operator_digest
    : null;
  const runtimeOutputPacket = trace?._runtime?.output_controller_packet &&
    typeof trace._runtime.output_controller_packet === 'object'
    ? trace._runtime.output_controller_packet
    : null;
  const topOutputPacket = trace?.output_controller_packet && typeof trace.output_controller_packet === 'object'
    ? trace.output_controller_packet
    : null;
  const outputControllerPacket = topOutputPacket || runtimeOutputPacket;
  const outputControllerKitchen = normalizeOutputControllerForKitchen(outputControllerPacket);
  if (!digest) {
    return {
      chain: [],
      eventLog: [],
      contourStatus: [],
      openGaps: [],
      flowKitchen: outputControllerKitchen ? { output: { controller: outputControllerKitchen } } : null,
      runtimeMonitor: null,
    };
  }
  const chain = Array.isArray(digest.chain) ? digest.chain.slice(0, 10) : [];
  const eventLog = Array.isArray(digest.event_log)
    ? digest.event_log.map(cleanThoughtLine).filter(Boolean).slice(0, 10)
    : [];
  const contourStatus = Array.isArray(digest.contour_status) ? digest.contour_status.slice(0, 12) : [];
  const openGaps = Array.isArray(digest.open_gaps)
    ? digest.open_gaps.map(cleanThoughtLine).filter(Boolean).slice(0, 8)
    : [];
  const flowKitchen = digest.flow_kitchen && typeof digest.flow_kitchen === 'object'
    ? digest.flow_kitchen
    : null;
  const mergedFlowKitchen = outputControllerKitchen && flowKitchen && !(flowKitchen.output?.controller)
    ? { ...flowKitchen, output: { ...(flowKitchen.output || {}), controller: outputControllerKitchen } }
    : flowKitchen;
  const runtimeMonitor = digest.runtime_monitor && typeof digest.runtime_monitor === 'object'
    ? digest.runtime_monitor
    : null;
  return { chain, eventLog, contourStatus, openGaps, flowKitchen: mergedFlowKitchen, runtimeMonitor };
};

const normalizeKitchenRows = (rows, limit = 8) => (
  Array.isArray(rows)
    ? rows.filter((row) => row && typeof row === 'object').slice(0, limit)
    : []
);

const kitchenRowText = (row) => {
  if (!row || typeof row !== 'object') return '';
  const idParts = [];
  for (const key of [
    'flow_id',
    'candidate_id',
    'packet_id',
    'action_id',
    'batch_id',
    'dispatch_id',
    'evidence_id',
    'tool_slot_id',
    'lease_id',
  ]) {
    const value = cleanThoughtLine(row[key]);
    if (value) idParts.push(`${key}=${value}`);
  }
  const summary = cleanThoughtLine(row.summary);
  return [idParts.join(', '), summary].filter(Boolean).join(' - ');
};

const hasKitchenRows = (rows) => normalizeKitchenRows(rows, 1).length > 0;

const hasOutputController = (kitchen) => {
  const controller = kitchen?.output?.controller;
  if (!controller || typeof controller !== 'object') return false;
  return Boolean(
    cleanThoughtLine(controller.packet_version) ||
    cleanThoughtLine(controller.status) ||
    cleanThoughtLine(controller.source) ||
    cleanThoughtLine(controller.surface_kind) ||
    cleanThoughtLine(controller.recipient) ||
    cleanThoughtLine(controller.interlocutor) ||
    cleanThoughtLine(controller.distribution) ||
    hasKitchenRows(controller.targets) ||
    cleanThoughtLine(controller.integrity) ||
    cleanThoughtLine(controller.llm)
  );
};

const ChatWindow = forwardRef(({ history, apiBase, debugTrace = false, liveTraceStatus = null }, ref) => {
  const [operatorTab, setOperatorTab] = useState('flows');

  const getImageSrc = (img) => {
    if (!img) return null;
    if (img.startsWith('blob:') || img.startsWith('data:')) return img;
    if (/^https?:\/\//i.test(img)) return img;
    const base = apiBase || 'http://127.0.0.1:18100';
    return img.startsWith('/') ? `${base}${img}` : `${base}/${img}`;
  };

  const safeStringify = (value) => {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  };

  const parseSummary = (summary) => {
    if (summary && typeof summary === 'string') {
      try {
        return JSON.parse(summary);
      } catch {
        return summary;
      }
    }
    return summary;
  };

  return (
    <Box className="chat-window" sx={{
      flexGrow: 1,
      overflowY: 'auto',
      p: 2,
      mb: 2,
      maxHeight: '60vh',
      scrollBehavior: 'smooth'
    }}>
      <RuntimeHud liveTraceStatus={liveTraceStatus} />

      {history.length === 0 && (
        <Typography variant="body2" className="chat-empty">
          Начни диалог!
        </Typography>
      )}

      {history.map((msg, index) => {
        if (isLowSignalRuntimeEvent(msg)) {
          return null;
        }
        const roleMap = {
          user: 'Вы',
          llm: 'ИИ',
          system: 'Система'
        };
        const isUser = msg.role === 'user';
        const isSystem = msg.role === 'system';
        const align = isUser ? 'right' : 'left';

        const isAuto = msg.origin === 'auto';
        const isManual = msg.origin === 'manual';
        const thoughtLines = buildVisibleThoughtLines(msg.trace);
        const hasThoughtWindow = debugTrace && !isUser && !isSystem && thoughtLines.length > 0;
        const nameLabel = isUser
          ? (msg.username || roleMap[msg.role] || 'Вы')
          : (roleMap[msg.role] || 'ИИ');
        const modelLabel = !isUser && msg.model ? ` (${msg.model})` : '';
        const visibleText = msg.text || msg.message || '[нет текста]';

        let timeStr = '';
        if (msg.timestamp) {
          try {
            const d = new Date(msg.timestamp);
            const dateStr = d.toLocaleDateString('ru-RU');
            const clockStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            timeStr = `${dateStr} ${clockStr}`;
          } catch (e) {}
        }

        const showReplyField = debugTrace && !isUser && !isSystem && !isAuto && !isManual && !String(msg.text || msg.message || '').trim();
        const traceQuestionItems = Array.isArray(msg.trace?.question_items)
          ? msg.trace.question_items.map((it) => String(it?.text || '').trim()).filter(Boolean).slice(0, 5)
          : [];
        const traceQuestions = traceQuestionItems.length > 0
          ? traceQuestionItems
          : (
            Array.isArray(msg.trace?.questions_to_user)
              ? msg.trace.questions_to_user.map((x) => String(x || '').trim()).filter(Boolean).slice(0, 5)
              : []
          );
        const traceAwaiting = !!(
          msg.trace?.awaiting_user_answer ||
          msg.trace?.user_interaction?.awaiting_user_answer
        );
        const traceToolPreflight = msg.trace?.tool_preflight && typeof msg.trace.tool_preflight === 'object'
          ? msg.trace.tool_preflight
          : null;
        const traceToolBranch = String(
          msg.trace?._runtime?.tool_preflight_branch ||
          ''
        ).trim();
        const traceToolDecision = String(
          msg.trace?._runtime?.tool_preflight_decision ||
          traceToolPreflight?.decision ||
          ''
        ).trim();
        const traceToolQuestion = String(
          msg.trace?._runtime?.tool_preflight_question ||
          ''
        ).trim();
        const operatorDigest = normalizeOperatorDigest(msg.trace);
        const kitchen = operatorDigest.flowKitchen || {};
        const runtimeMonitor = operatorDigest.runtimeMonitor || null;
        const monitorFlows = normalizeKitchenRows(runtimeMonitor?.flows, 24);
        const monitorTabs = normalizeKitchenRows(runtimeMonitor?.tabs, 6);
        const activeOperatorTab = monitorTabs.some((tab) => tab.id === operatorTab)
          ? operatorTab
          : String(runtimeMonitor?.active_tab || 'flows');
        const showRuntimeMonitor = !isUser && !!runtimeMonitor && (
          monitorFlows.length > 0 ||
          cleanThoughtLine(runtimeMonitor.current_ocm_label) ||
          monitorTabs.length > 0
        );
        const showKitchen = !isUser && !!operatorDigest.flowKitchen && (
          hasKitchenRows(kitchen.packets) ||
          hasKitchenRows(kitchen.candidates) ||
          hasKitchenRows(kitchen.flows) ||
          hasKitchenRows(kitchen.delivery?.obligations) ||
          hasKitchenRows(kitchen.l1_command?.action_commands) ||
          hasKitchenRows(kitchen.egress?.branch_dispatches) ||
          hasKitchenRows(kitchen.action?.work_orders) ||
          hasKitchenRows(kitchen.action?.feedback) ||
          hasKitchenRows(kitchen.action?.visual_results) ||
          hasKitchenRows(kitchen.reality?.evidence) ||
          hasOutputController(kitchen)
        );
        const showOperatorDigest = !isUser && (
          operatorDigest.chain.length > 0 ||
          operatorDigest.eventLog.length > 0 ||
          operatorDigest.contourStatus.length > 0 ||
          operatorDigest.openGaps.length > 0 ||
          showKitchen ||
          showRuntimeMonitor
        );
        const showToolPreflightBlock = debugTrace && !isUser && !!(traceToolPreflight || traceToolDecision || traceToolQuestion);
        const showAskUserBlock = debugTrace && !isUser && traceAwaiting;
        const rowClass = `chat-row ${isUser ? 'is-user' : isSystem ? 'is-system' : 'is-llm'}`;
        const bubbleClass = `chat-bubble ${isUser ? 'chat-bubble--user' : isSystem ? 'chat-bubble--system' : 'chat-bubble--llm'}`;
        const compactRuntimeEvent = isCompactRuntimeSystemEvent(msg);

        if (compactRuntimeEvent) {
          const runtimeThreadId = String(msg.meta?.thread_id || msg.thread_id || '').trim();
          const visibleRuntimeThreadId = runtimeThreadId && runtimeThreadId !== 'runtime-monitor'
            ? runtimeThreadId.slice(0, 28)
            : '';
          return (
            <Box key={index} className="runtime-event-row" data-testid="runtime-event-row">
              <div className="runtime-event-row__meta">
                <span className="runtime-event-row__author">{nameLabel}</span>
                {visibleRuntimeThreadId ? <span className="runtime-event-row__author">· {visibleRuntimeThreadId}</span> : null}
                {timeStr ? <span className="runtime-event-row__time">{timeStr}</span> : null}
              </div>
              <div className="runtime-event-row__text">{visibleText}</div>
            </Box>
          );
        }

        return (
          <Box key={index} className={rowClass} sx={{ textAlign: align, mb: 2 }}>
            <Box className={bubbleClass}>
              <Box className="chat-bubble__meta">
                <span className="chat-bubble__author">{nameLabel}{modelLabel}</span>
                {timeStr ? <span className="chat-bubble__time">{timeStr}</span> : null}
              </Box>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: isUser ? 600 : 400,
                  fontStyle: isSystem ? 'italic' : 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                  lineHeight: 1.55,
                  textAlign: 'left'
                }}
              >
                {visibleText}
              </Typography>
            </Box>

            {debugTrace && (isAuto || isManual) && (
              <Typography variant="caption" className="chat-origin-tag" sx={{ display: 'block', mt: 0.6 }}>
                {isAuto ? 'Авто‑тик' : 'Ручной тик'}
              </Typography>
            )}

            {hasThoughtWindow && (
              <Box className="thought-window">
                <Typography variant="caption" className="thought-window__kicker">
                  Окно мышления (кратко)
                </Typography>
                <div className="thought-window__body">
                  {thoughtLines.map((line, i) => (
                    <div key={`${index}-thought-${i}`}>• {line}</div>
                  ))}
                </div>
              </Box>
            )}

            {showReplyField && (
              <Box className="trace-card trace-card--reply">
                <Typography variant="caption" className="trace-card__kicker">
                  Ответ пользователю
                </Typography>
                <div className="trace-card__body">
                  {msg.text || msg.message || '[нет текста]'}
                </div>
              </Box>
            )}

            {showAskUserBlock && (
              <Box className="trace-card trace-card--ask">
                <Typography variant="caption" className="trace-card__kicker">
                  Текущие вопросы к пользователю
                </Typography>
                <div className="trace-card__body">
                  {traceQuestions.length > 0
                    ? traceQuestions.map((q, i) => <div key={`${index}-ask-${i}`}>• {q}</div>)
                    : <div>• Ожидается ответ пользователя.</div>}
                </div>
              </Box>
            )}

            {showToolPreflightBlock && (
              <Box className="trace-card trace-card--tool">
                <Typography variant="caption" className="trace-card__kicker">
                  Tool Preflight
                </Typography>
                <div className="trace-card__body">
                  <div>decision: {traceToolDecision || 'idle'}</div>
                  <div>branch: {traceToolBranch || 'none'}</div>
                  {traceToolQuestion ? <div>question: {traceToolQuestion}</div> : null}
                </div>
              </Box>
            )}

            {showOperatorDigest && (
              <Box className="trace-card trace-card--operator" data-testid="operator-digest">
                {showRuntimeMonitor && (
                  <>
                    <Typography variant="caption" className="trace-card__kicker">
                      Runtime monitor
                    </Typography>
                    <div className="ocm-monitor" data-testid="runtime-monitor-tabs">
                      <div className="ocm-monitor__head">
                        <span className="ocm-monitor__time">
                          {runtimeMonitor.current_ocm_label || 'OCM n/a'}
                        </span>
                        {runtimeMonitor.current?.foreground_flow_id ? (
                          <span className="ocm-monitor__meta">
                            current flow: {runtimeMonitor.current.foreground_flow_id}
                          </span>
                        ) : null}
                        {runtimeMonitor.current?.flow_count !== undefined ? (
                          <span className="ocm-monitor__meta">
                            flows: {runtimeMonitor.current.flow_count}
                          </span>
                        ) : null}
                      </div>
                      {monitorTabs.length > 0 && (
                        <div className="ocm-monitor__tabs" role="tablist" aria-label="runtime monitor tabs">
                          {monitorTabs.map((tab) => {
                            const tabId = String(tab.id || '').trim() || 'flows';
                            const selected = activeOperatorTab === tabId;
                            return (
                              <button
                                key={`${index}-monitor-tab-${tabId}`}
                                type="button"
                                className={`ocm-monitor__tab${selected ? ' is-active' : ''}`}
                                onClick={() => setOperatorTab(tabId)}
                                aria-pressed={selected}
                              >
                                {tab.label || tabId}
                                {tab.count !== undefined ? <span>{tab.count}</span> : null}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {activeOperatorTab === 'flows' && (
                        <div className="ocm-flow-list">
                          {monitorFlows.map((flow) => (
                            <div
                              key={`${index}-monitor-flow-${flow.number || flow.flow_id}`}
                              className={`ocm-flow-row${flow.is_current ? ' is-current' : ''}`}
                            >
                              <div className="ocm-flow-row__head">
                                <span className="ocm-flow-row__number">#{flow.number || '?'}</span>
                                <span className="ocm-flow-row__id">{flow.flow_id_short || flow.flow_id || 'flow'}</span>
                                {flow.lane ? <span className="ocm-flow-row__badge">{flow.lane}</span> : null}
                                {flow.last_processed_ocm !== undefined && flow.last_processed_ocm !== null ? (
                                  <span className="ocm-flow-row__ocm">last OCM {flow.last_processed_ocm}</span>
                                ) : null}
                                {flow.needs_l1_now ? <span className="ocm-flow-row__badge">L1</span> : null}
                              </div>
                              <div className="ocm-flow-row__content">
                                {flow.content || flow.summary || 'no content'}
                              </div>
                              <div className="ocm-flow-row__foot">
                                {flow.flow_kind ? <span>{flow.flow_kind}</span> : null}
                                {flow.lifecycle_state ? <span>{flow.lifecycle_state}</span> : null}
                                {flow.origin_ocm !== undefined && flow.origin_ocm !== null ? <span>origin OCM {flow.origin_ocm}</span> : null}
                                {flow.age_ocm !== undefined && flow.age_ocm !== null ? <span>age {flow.age_ocm}</span> : null}
                                {Array.isArray(flow.source_owners) && flow.source_owners.length > 0 ? (
                                  <span>{flow.source_owners.join(', ')}</span>
                                ) : null}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {activeOperatorTab === 'chain' && operatorDigest.chain.length > 0 && (
                        <div className="trace-card__body">
                          {operatorDigest.chain.map((step, i) => (
                            <div key={`${index}-monitor-chain-${i}`}>
                              {step?.step || i + 1}. {step?.owner || 'unknown'}: {step?.status || 'unknown'} - {step?.summary || ''}
                            </div>
                          ))}
                        </div>
                      )}
                      {activeOperatorTab === 'status' && operatorDigest.contourStatus.length > 0 && (
                        <div className="trace-card__body">
                          {operatorDigest.contourStatus.map((item, i) => (
                            <div key={`${index}-monitor-status-${i}`}>
                              {item?.contour || 'unknown'}: {item?.state || 'unknown'} - {item?.proof || ''}
                            </div>
                          ))}
                        </div>
                      )}
                      {activeOperatorTab === 'gaps' && operatorDigest.openGaps.length > 0 && (
                        <div className="trace-card__body">
                          {operatorDigest.openGaps.map((line, i) => (
                            <div key={`${index}-monitor-gap-${i}`}>• {line}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
                {operatorDigest.chain.length > 0 && (
                  <>
                    <Typography variant="caption" className="trace-card__kicker">
                      Цепочка выполнения
                    </Typography>
                    <div className="trace-card__body">
                      {operatorDigest.chain.map((step, i) => (
                        <div key={`${index}-chain-${i}`}>
                          {step?.step || i + 1}. {step?.owner || 'unknown'}: {step?.status || 'unknown'} - {step?.summary || ''}
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {showKitchen && (
                  <>
                    <Typography variant="caption" className="trace-card__kicker">
                      Внутренняя кухня
                    </Typography>
                    <div className="trace-card__body">
                      {hasKitchenRows(kitchen.packets) && (
                        <div>
                          <strong>packets:</strong>
                          {normalizeKitchenRows(kitchen.packets, 8).map((row, i) => (
                            <div key={`${index}-k-packet-${i}`}>- {kitchenRowText(row)}</div>
                          ))}
                        </div>
                      )}
                      {hasKitchenRows(kitchen.candidates) && (
                        <div>
                          <strong>candidates:</strong>
                          {normalizeKitchenRows(kitchen.candidates, 8).map((row, i) => (
                            <div key={`${index}-k-candidate-${i}`}>- {kitchenRowText(row)}</div>
                          ))}
                        </div>
                      )}
                      {hasKitchenRows(kitchen.flows) && (
                        <div>
                          <strong>flows:</strong>
                          {normalizeKitchenRows(kitchen.flows, 8).map((row, i) => (
                            <div key={`${index}-k-flow-${i}`}>- {kitchenRowText(row)}</div>
                          ))}
                        </div>
                      )}
                      {kitchen.delivery?.foreground_flow_id ? (
                        <div><strong>foreground:</strong> {kitchen.delivery.foreground_flow_id}</div>
                      ) : null}
                      {hasKitchenRows(kitchen.delivery?.obligations) && (
                        <div>
                          <strong>L1 delivery:</strong>
                          {normalizeKitchenRows(kitchen.delivery.obligations, 6).map((row, i) => (
                            <div key={`${index}-k-obligation-${i}`}>- {kitchenRowText(row)}</div>
                          ))}
                        </div>
                      )}
                      {hasKitchenRows(kitchen.l1_command?.action_commands) && (
                        <div>
                          <strong>L1 action commands:</strong>
                          {normalizeKitchenRows(kitchen.l1_command.action_commands, 6).map((row, i) => (
                            <div key={`${index}-k-l1-action-${i}`}>- {kitchenRowText(row)}</div>
                          ))}
                        </div>
                      )}
                      {kitchen.l1_command?.next_ocm ? (
                        <div><strong>next OCM:</strong> {kitchen.l1_command.next_ocm}</div>
                      ) : null}
                      {hasKitchenRows(kitchen.egress?.branch_dispatches) && (
                        <div>
                          <strong>egress branch dispatch:</strong>
                          {normalizeKitchenRows(kitchen.egress.branch_dispatches, 6).map((row, i) => (
                            <div key={`${index}-k-egress-${i}`}>- {kitchenRowText(row)}</div>
                          ))}
                        </div>
                      )}
                      {hasKitchenRows(kitchen.action?.work_orders) && (
                        <div>
                          <strong>action work orders:</strong>
                          {normalizeKitchenRows(kitchen.action.work_orders, 6).map((row, i) => (
                            <div key={`${index}-k-work-${i}`}>- {kitchenRowText(row)}</div>
                          ))}
                        </div>
                      )}
                      {hasKitchenRows(kitchen.action?.feedback) && (
                        <div>
                          <strong>action feedback:</strong>
                          {normalizeKitchenRows(kitchen.action.feedback, 6).map((row, i) => (
                            <div key={`${index}-k-feedback-${i}`}>- {kitchenRowText(row)}</div>
                          ))}
                        </div>
                      )}
                      {hasKitchenRows(kitchen.action?.visual_results) && (
                        <div>
                          <strong>action visual result:</strong>
                          {normalizeKitchenRows(kitchen.action.visual_results, 6).map((row, i) => (
                            <div key={`${index}-k-visual-result-${i}`}>- {kitchenRowText(row)}</div>
                          ))}
                        </div>
                      )}
                      {hasKitchenRows(kitchen.reality?.evidence) && (
                        <div>
                          <strong>reality evidence:</strong>
                          {normalizeKitchenRows(kitchen.reality.evidence, 6).map((row, i) => (
                            <div key={`${index}-k-evidence-${i}`}>- {kitchenRowText(row)}</div>
                          ))}
                        </div>
                      )}
                      {hasOutputController(kitchen) && (
                        <div>
                          <strong>output controller:</strong>
                          <div>
                            - packet={kitchen.output.controller.packet_version || 'n/a'},
                            status={kitchen.output.controller.status || 'n/a'},
                            source={kitchen.output.controller.source || 'n/a'},
                            surface={kitchen.output.controller.surface_kind || 'n/a'}
                          </div>
                          {kitchen.output.controller.integrity ? (
                            <div>- integrity: {kitchen.output.controller.integrity}</div>
                          ) : null}
                          {kitchen.output.controller.recipient ? (
                            <div>- recipient: {kitchen.output.controller.recipient}</div>
                          ) : null}
                          {kitchen.output.controller.interlocutor ? (
                            <div>- interlocutor: {kitchen.output.controller.interlocutor}</div>
                          ) : null}
                          {kitchen.output.controller.distribution ? (
                            <div>- distribution: {kitchen.output.controller.distribution}</div>
                          ) : null}
                          {hasKitchenRows(kitchen.output.controller.targets) ? (
                            <div>
                              <strong>output targets:</strong>
                              {normalizeKitchenRows(kitchen.output.controller.targets, 6).map((row, i) => (
                                <div key={`${index}-k-output-target-${i}`}>- {kitchenRowText(row)}</div>
                              ))}
                            </div>
                          ) : null}
                          {kitchen.output.controller.llm ? (
                            <div>- llm: {kitchen.output.controller.llm}</div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </>
                )}
                {operatorDigest.eventLog.length > 0 && (
                  <>
                    <Typography variant="caption" className="trace-card__kicker">
                      Лог цикла
                    </Typography>
                    <div className="trace-card__body">
                      {operatorDigest.eventLog.map((line, i) => (
                        <div key={`${index}-event-${i}`}>{i + 1}. {line}</div>
                      ))}
                    </div>
                  </>
                )}
                {operatorDigest.contourStatus.length > 0 && (
                  <>
                    <Typography variant="caption" className="trace-card__kicker">
                      Что работает / что нет
                    </Typography>
                    <div className="trace-card__body">
                      {operatorDigest.contourStatus.map((item, i) => (
                        <div key={`${index}-contour-${i}`}>
                          {item?.contour || 'unknown'}: {item?.state || 'unknown'} - {item?.proof || ''}
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {operatorDigest.openGaps.length > 0 && (
                  <>
                    <Typography variant="caption" className="trace-card__kicker">
                      Открытые разрывы
                    </Typography>
                    <div className="trace-card__body">
                      {operatorDigest.openGaps.map((line, i) => (
                        <div key={`${index}-gap-${i}`}>• {line}</div>
                      ))}
                    </div>
                  </>
                )}
              </Box>
            )}

            {debugTrace && !isUser && (msg.topic || msg.weight !== undefined) && (
              <Typography
                variant="caption"
                className="chat-meta-note"
                sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}
              >
                {msg.topic && <>🧠 <strong>Тема:</strong> {msg.topic}. </>}
                {msg.weight !== undefined && <>⚖️ <strong>Вес:</strong> {Number(msg.weight).toFixed(3)}</>}
              </Typography>
            )}

            {msg.image && (
              <Box sx={{ mt: 1 }}>
                <img
                  src={getImageSrc(msg.image)}
                  alt="изображение"
                  className="chat-image"
                  onError={e => { e.target.style.display = 'none'; }}
                />
              </Box>
            )}

            {debugTrace && msg.trace && (
              <Box sx={{ mt: 1, textAlign: 'left' }} className="trace-panel">
                <details>
                  <summary>Цепочка ролей (summary)</summary>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" className="trace-label">
                      reply_text
                    </Typography>
                    <pre className="trace-pre">
                      {msg.trace.reply_text || ''}
                    </pre>
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" className="trace-label">
                      questions_to_user
                    </Typography>
                    <pre className="trace-pre">
                      {safeStringify({
                        awaiting_user_answer: !!msg.trace.awaiting_user_answer,
                        question_items: (msg.trace.question_items || []).slice(0, 5),
                        questions_to_user: (msg.trace.questions_to_user || []).slice(0, 5),
                        user_interaction: msg.trace.user_interaction || {}
                      })}
                    </pre>
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" className="trace-label">
                      tool_preflight
                    </Typography>
                    <pre className="trace-pre">
                      {safeStringify({
                        tool_preflight: msg.trace.tool_preflight || {},
                        tool_preflight_branch: msg.trace?._runtime?.tool_preflight_branch || '',
                        tool_preflight_question: msg.trace?._runtime?.tool_preflight_question || '',
                      })}
                    </pre>
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" className="trace-label">
                      memory_writes (top)
                    </Typography>
                    <pre className="trace-pre">
                      {safeStringify((msg.trace.memory_writes || []).slice(0, 8))}
                    </pre>
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" className="trace-label">
                      actions (top)
                    </Typography>
                    <pre className="trace-pre">
                      {safeStringify((msg.trace.actions || []).slice(0, 8))}
                    </pre>
                  </Box>
                  {msg.trace.suture && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" className="trace-label">
                        SUTURE observer
                      </Typography>
                      <pre className="trace-pre">
                        {safeStringify(msg.trace.suture)}
                      </pre>
                    </Box>
                  )}
                  {msg.trace.rivet && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" className="trace-label">
                        RIVET observer
                      </Typography>
                      <pre className="trace-pre">
                        {safeStringify(msg.trace.rivet)}
                      </pre>
                    </Box>
                  )}
                  {msg.trace.ocm_outcome_handoff && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" className="trace-label">
                        OCM Outcome Handoff
                      </Typography>
                      <pre className="trace-pre">
                        {safeStringify(msg.trace.ocm_outcome_handoff)}
                      </pre>
                    </Box>
                  )}
                  {msg.trace.continuation_diff && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" className="trace-label">
                        Continuation Diff
                      </Typography>
                      <pre className="trace-pre">
                        {safeStringify(msg.trace.continuation_diff)}
                      </pre>
                    </Box>
                  )}
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" className="trace-label">
                      roles
                    </Typography>
                    {(msg.trace.roles || []).map((r, i) => {
                      const parsed = r.parsed ?? parseSummary(r.summary);
                      return (
                        <Box key={`${r.role}-${i}`} sx={{ mb: 1 }}>
                          <Typography variant="caption" className="trace-label">
                            {r.role} {r.valid_json === false ? '(invalid_json)' : ''}
                          </Typography>
                          <pre className="trace-pre">
                            {safeStringify(parsed)}
                          </pre>
                        </Box>
                      );
                    })}
                  </Box>
                </details>
              </Box>
            )}
          </Box>
        );
      })}

      <div ref={ref}></div>
    </Box>
  );
});

export default ChatWindow;
